import { useContext, useEffect, useMemo } from 'react'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchAccumulatedOfferById, fetchAvailableRatesForOffer } from 'actions/OfferActions'
import { fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import { fetchExperiencesById, fetchExperienceTimes } from 'actions/ExperienceActions'
import CurrencyContext from 'contexts/currencyContext'
import { unique } from 'lib/array/arrayUtils'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { isLEHotel } from 'lib/offer/offerTypes'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { isFetchTimesParamsValid } from 'lib/experiences/experienceUtils'
import { isHotelSavedData, isExperienceSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import type { TripItineraryData, TripItineraryItem, TripItemWithoutDay } from 'tripPlanner/api/types'
import {
  getCartPanelExperienceTimesKey,
  isExperienceBuyNowBookLaterBookingType,
} from '../utils/cartPanelUtils'

interface BookableItemForFetch {
  productType: 'accommodation'
  offerId: string
  packageId: string | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  occupancy: App.Occupants | undefined
  duration: number | undefined
  roomRateId: string | undefined
}

function isCalendarHotelPackage(pkg: App.Package): pkg is App.HotelPackage {
  return !!pkg.roomType
}

export interface ExperienceItemForFetch {
  productType: 'experience'
  experienceId: string
  date: string | undefined
  pickupPointId: string | undefined
  redemptionLocationId: string | undefined
  isBuyNowBookLater: boolean
  hasSelectedTickets: boolean
}

function getExperienceItemTimesKey(item: ExperienceItemForFetch, currency: string): string {
  return getCartPanelExperienceTimesKey(item, currency)
}

export function mergeExperienceItemsForTimesFetch(
  items: Array<ExperienceItemForFetch>,
  currency: string,
): Array<ExperienceItemForFetch> {
  const itemsByKey = new Map<string, ExperienceItemForFetch>()

  for (const item of items) {
    const key = getExperienceItemTimesKey(item, currency)
    const existing = itemsByKey.get(key)
    if (!existing) {
      itemsByKey.set(key, item)
      continue
    }
    if (!existing.hasSelectedTickets && item.hasSelectedTickets) {
      itemsByKey.set(key, { ...existing, hasSelectedTickets: true })
    }
  }

  return Array.from(itemsByKey.values())
}

function extractBookableItems(itineraryData: TripItineraryData | undefined): {
  accommodationItems: Array<BookableItemForFetch>
  experienceItems: Array<ExperienceItemForFetch>
} {
  if (!itineraryData?.stops) return { accommodationItems: [], experienceItems: [] }

  const accommodationItems: Array<BookableItemForFetch> = []
  const experienceItems: Array<ExperienceItemForFetch> = []
  const seenItemIds = new Set<number>()

  const processItem = (item: TripItineraryItem | TripItemWithoutDay) => {
    if (seenItemIds.has(item.id)) return

    const sd = item.savedItemData
    if (item.type === 'ACCOMMODATION' && isHotelSavedData(sd)) {
      seenItemIds.add(item.id)
      accommodationItems.push({
        productType: 'accommodation',
        offerId: sd.offerId,
        packageId: sd.packageId,
        checkIn: sd.checkIn,
        checkOut: sd.checkOut,
        occupancy: sd.occupancies?.[0],
        duration: sd.packageDuration,
        roomRateId: sd.roomRateId,
      })
      return
    }

    if (item.type === 'EXPERIENCE' && isExperienceSavedData(sd)) {
      seenItemIds.add(item.id)
      experienceItems.push({
        productType: 'experience',
        experienceId: sd.experienceId,
        date: sd.date,
        pickupPointId: sd.pickupPointId,
        redemptionLocationId: sd.redemptionLocationId,
        isBuyNowBookLater: isExperienceBuyNowBookLaterBookingType(sd.bookingType),
        hasSelectedTickets: !!sd.tickets?.some(ticket => ticket.count > 0),
      })
    }
  }

  for (const stop of itineraryData.stops) {
    for (const day of stop.days) {
      for (const item of day.items) {
        processItem(item)
      }
    }
    for (const item of stop.itemsWithoutDay ?? []) {
      processItem(item)
    }
  }

  return { accommodationItems, experienceItems }
}

export function useFetchCartPanelOffers(itineraryData: TripItineraryData | undefined): void {
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)

  const { accommodationItems, experienceItems } = useMemo(
    () => extractBookableItems(itineraryData),
    [itineraryData],
  )

  const offerIds = useMemo(
    () => unique(accommodationItems.map(item => item.offerId)),
    [accommodationItems],
  )

  const experienceIds = useMemo(
    () => unique(experienceItems.map(item => item.experienceId)),
    [experienceItems],
  )

  useEffect(() => {
    for (const offerId of offerIds) {
      dispatch(fetchAccumulatedOfferById(offerId))
    }
  }, [dispatch, offerIds])

  useEffect(() => {
    if (experienceIds.length > 0) {
      dispatch(fetchExperiencesById(...experienceIds))
    }
  }, [dispatch, experienceIds])

  const { offers, calendarErrors, experiences } = useAppSelector(state => ({
    offers: state.offer.offers,
    calendarErrors: state.calendar.calendarErrors,
    experiences: state.experience.experiences,
  }), shallowEqual)

  useEffect(() => {
    for (const item of accommodationItems) {
      if (!item.checkIn || !item.checkOut || !item.occupancy || !item.packageId) continue

      const offer = offers[item.offerId]
      if (!offer || !isLEHotel(offer)) continue

      const packageKey = getPackageUniqueKey(item.packageId, item.duration ?? 0, item.roomRateId ?? '')
      const packages = offer.packages.filter((pkg): pkg is App.HotelPackage => pkg.uniqueKey === packageKey && isCalendarHotelPackage(pkg))
      if (packages.length === 0 || calendarErrors[packageKey]) continue

      dispatch(fetchCalendarsByOccupancy(offer.id, {
        occupants: [item.occupancy],
        packages,
      }))

      if (offer.type === OFFER_TYPE_ALWAYS_ON) {
        dispatch(fetchAvailableRatesForOffer(offer, {
          occupants: [item.occupancy],
          checkIn: item.checkIn,
          checkOut: item.checkOut,
        }))
      }
    }
  }, [dispatch, accommodationItems, offers, calendarErrors])

  const itemsToFetchTimesFor = useMemo(
    () => mergeExperienceItemsForTimesFetch(experienceItems, currency),
    [experienceItems, currency],
  )

  useEffect(() => {
    for (const item of itemsToFetchTimesFor) {
      const experience = experiences[item.experienceId]
      const hasValidSearchParams = !!experience && isFetchTimesParamsValid(experience, {
        date: item.date,
        pickupPointId: item.pickupPointId,
        redemptionLocationId: item.redemptionLocationId,
        isBuyNowBookLater: item.isBuyNowBookLater,
      })

      // Match checkout/user-cart: selected cart tickets fetch availability even when search-style params such as date are absent.
      if (!item.hasSelectedTickets && !hasValidSearchParams) continue

      dispatch(fetchExperienceTimes(item.experienceId, {
        currency,
        date: item.date,
        pickupPointId: item.pickupPointId,
        redemptionLocationId: item.redemptionLocationId,
        isBuyNowBookLater: item.isBuyNowBookLater,
        ignoreCache: item.hasSelectedTickets,
      }))
    }
  }, [dispatch, itemsToFetchTimesFor, experiences, currency])
}
